import React, { useState, useMemo } from "react";
import { C, ORG, ROLE_NAMES, ROLE_PERMISSIONS, getDailyQuote } from "./constants";
import { Card, KPICard } from "./erp-shared";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend, Area, AreaChart
} from "recharts";
import {
  LayoutDashboard, Users2, Globe, Landmark, Heart, CalendarDays,
  Wallet, HeartHandshake, TrendingUp, Briefcase, Megaphone,
  HandHelping, Shield, UserCog, ArrowUpRight, ArrowDownRight,
  Clock, CheckCircle, AlertCircle, FileText, DollarSign,
  BookOpen, Mic, MapPin, Star, Target, Activity,
  ChevronLeft, BarChart3, PieChart as PieChartIcon
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ Dashboard Page
   ═════════════════════════════════════════════ */
export default function DashboardPage({ user, onNavigate }) {
  const quote = getDailyQuote();
  const perms = ROLE_PERMISSIONS[user.role] || {};
  const isAdmin = perms.adminOnly;

  return (
    <div style={{ direction: "rtl" }}>
      {/* ─── Welcome banner ─── */}
      <div style={styles.welcomeBanner}>
        <div style={styles.welcomeContent}>
          <div>
            <h2 style={styles.welcomeTitle}>
              أهلاً، {user.name} 👋
            </h2>
            <p style={styles.welcomeSubtitle}>
              {ROLE_NAMES[user.role]} — {ORG.shortName}
            </p>
          </div>
          <div style={styles.quoteCard}>
            <p style={styles.quoteText}>"{quote.text}"</p>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, margin: "4px 0 0" }}>— {quote.source}</p>
          </div>
        </div>
        <div style={styles.welcomeDeco} />
      </div>

      {/* ─── KPI Grid ─── */}
      <div style={styles.kpiGrid}>
        <KPICard title="إجمالي المستفيدين" value="12,480" icon={Users2} color={C.teal} trend="+8.2%" />
        <KPICard title="البرامج النشطة" value="34" icon={Target} color="#7c3aed" trend="+3" />
        <KPICard title="الإيرادات (ريال)" value="2,450,000" icon={DollarSign} color={C.success} trend="+12%" />
        <KPICard title="المصروفات (ريال)" value="1,870,000" icon={Wallet} color={C.warning} trend="-5%" />
        <KPICard title="المتطوعون" value="186" icon={HandHelping} color="#059669" trend="+22" />
        <KPICard title="الدعاة" value="18" icon={Mic} color="#2563eb" trend="0" />
        <KPICard title="المساجد" value="47" icon={Landmark} color="#0891b2" trend="+2" />
        <KPICard title="الأحداث القادمة" value="8" icon={CalendarDays} color="#ea580c" />
      </div>

      {/* ─── Charts Row ─── */}
      <div style={styles.chartsRow}>
        {/* Monthly Revenue vs Expenses */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart3 size={18} color={C.teal} />
              <span style={styles.chartTitle}>الإيرادات والمصروفات الشهرية</span>
            </div>
            <span style={styles.chartYear}>1446 هـ</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={MONTHLY_FINANCE} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v.toLocaleString()} ريال`]}
              />
              <Bar dataKey="income" name="الإيرادات" fill={C.teal} radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" name="المصروفات" fill={C.accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Beneficiaries by Department */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PieChartIcon size={18} color="#7c3aed" />
              <span style={styles.chartTitle}>المستفيدون حسب القسم</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={DEPT_DATA} cx="50%" cy="50%" innerRadius={55} outerRadius={95} dataKey="value" paddingAngle={3}>
                {DEPT_DATA.map((entry, i) => (
                  <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v.toLocaleString()} مستفيد`]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {DEPT_DATA.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: DEPT_COLORS[i] }} />
                <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>{d.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ─── Growth Trend + Quick Actions ─── */}
      <div style={styles.chartsRow}>
        {/* Monthly Growth */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Activity size={18} color={C.success} />
              <span style={styles.chartTitle}>نمو المستفيدين الشهري</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={GROWTH_DATA}>
              <defs>
                <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.teal} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={C.teal} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }} />
              <Area type="monotone" dataKey="count" stroke={C.teal} fill="url(#growthGrad)" strokeWidth={2.5} name="مستفيد" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Quick Actions */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Star size={18} color={C.accent} />
              <span style={styles.chartTitle}>إجراءات سريعة</span>
            </div>
          </div>
          <div style={styles.quickGrid}>
            {QUICK_ACTIONS.filter(a => !a.adminOnly || isAdmin).map((action, i) => (
              <button
                key={i}
                onClick={() => onNavigate && onNavigate(action.module)}
                style={styles.quickBtn}
                title={action.label}
              >
                <div style={{ ...styles.quickIcon, background: `${action.color}15` }}>
                  <action.icon size={20} color={action.color} />
                </div>
                <span style={styles.quickLabel}>{action.label}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* ─── Recent Activity + Upcoming Events ─── */}
      <div style={styles.chartsRow}>
        {/* Recent Activity */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Clock size={18} color={C.primary} />
              <span style={styles.chartTitle}>آخر النشاطات</span>
            </div>
          </div>
          <div style={styles.activityList}>
            {RECENT_ACTIVITIES.map((act, i) => (
              <div key={i} style={styles.activityItem}>
                <div style={{
                  ...styles.activityDot,
                  background: act.type === "success" ? C.success : act.type === "warning" ? C.warning : act.type === "info" ? C.teal : C.primary,
                }} />
                <div style={{ flex: 1 }}>
                  <p style={styles.activityText}>{act.text}</p>
                  <span style={styles.activityTime}>{act.time}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Upcoming Events */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CalendarDays size={18} color="#ea580c" />
              <span style={styles.chartTitle}>الأحداث القادمة</span>
            </div>
          </div>
          <div style={styles.eventsList}>
            {UPCOMING_EVENTS.map((ev, i) => (
              <div key={i} style={styles.eventItem}>
                <div style={{ ...styles.eventDate, borderColor: ev.color }}>
                  <span style={styles.eventDay}>{ev.day}</span>
                  <span style={styles.eventMonth}>{ev.monthName}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={styles.eventTitle}>{ev.title}</p>
                  <p style={styles.eventDetail}>
                    <MapPin size={12} /> {ev.location} — {ev.time}
                  </p>
                </div>
                <div style={{ ...styles.eventTypeBadge, background: `${ev.color}15`, color: ev.color }}>
                  {ev.type}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ─── Department Summary (Admin only) ─── */}
      {isAdmin && (
        <Card style={{ marginTop: 20, padding: 24 }}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <LayoutDashboard size={18} color={C.primary} />
              <span style={styles.chartTitle}>ملخص الأقسام</span>
            </div>
          </div>
          <div style={styles.deptGrid}>
            {DEPT_SUMMARY.map((dept, i) => (
              <div key={i} style={styles.deptCard}>
                <div style={{ ...styles.deptIcon, background: `${dept.color}15` }}>
                  <dept.icon size={22} color={dept.color} />
                </div>
                <div>
                  <p style={styles.deptName}>{dept.name}</p>
                  <p style={styles.deptStat}>{dept.stat}</p>
                </div>
                <div style={{
                  ...styles.deptTrend,
                  color: dept.trend.startsWith("+") ? C.success : dept.trend.startsWith("-") ? C.danger : C.textLight,
                }}>
                  {dept.trend.startsWith("+") ? <ArrowUpRight size={14} /> : dept.trend.startsWith("-") ? <ArrowDownRight size={14} /> : null}
                  {dept.trend}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const MONTHLY_FINANCE = [
  { month: "محرم", income: 280000, expense: 210000 },
  { month: "صفر", income: 310000, expense: 240000 },
  { month: "ربيع1", income: 350000, expense: 260000 },
  { month: "ربيع2", income: 290000, expense: 230000 },
  { month: "جمادى1", income: 380000, expense: 280000 },
  { month: "جمادى2", income: 420000, expense: 310000 },
  { month: "رجب", income: 360000, expense: 290000 },
  { month: "شعبان", income: 450000, expense: 320000 },
  { month: "رمضان", income: 680000, expense: 380000 },
  { month: "شوال", income: 320000, expense: 270000 },
  { month: "ذوالقعدة", income: 340000, expense: 250000 },
  { month: "ذوالحجة", income: 410000, expense: 300000 },
];

const DEPT_DATA = [
  { name: "الجاليات", value: 5200 },
  { name: "المساجد", value: 3100 },
  { name: "النسائي", value: 1800 },
  { name: "التطوع", value: 1400 },
  { name: "الأحداث", value: 980 },
];
const DEPT_COLORS = ["#2563eb", "#0891b2", "#db2777", "#059669", "#ea580c"];

const GROWTH_DATA = [
  { month: "محرم", count: 8200 },
  { month: "صفر", count: 8600 },
  { month: "ربيع1", count: 9100 },
  { month: "ربيع2", count: 9400 },
  { month: "جمادى1", count: 9900 },
  { month: "جمادى2", count: 10300 },
  { month: "رجب", count: 10800 },
  { month: "شعبان", count: 11200 },
  { month: "رمضان", count: 12100 },
  { month: "شوال", count: 11900 },
  { month: "ذوالقعدة", count: 12200 },
  { month: "ذوالحجة", count: 12480 },
];

const QUICK_ACTIONS = [
  { label: "إضافة متبرع", icon: HeartHandshake, color: "#d97706", module: "donors" },
  { label: "تسجيل مصروف", icon: Wallet, color: "#16a34a", module: "finance" },
  { label: "إضافة متطوع", icon: HandHelping, color: "#059669", module: "volunteer" },
  { label: "تقرير مالي", icon: FileText, color: C.primary, module: "finance", adminOnly: true },
  { label: "إدارة المستخدمين", icon: UserCog, color: "#475569", module: "users", adminOnly: true },
];

const RECENT_ACTIVITIES = [
  { text: "تم اعتماد ميزانية الربع الثالث", time: "منذ ساعة", type: "success" },
  { text: "إضافة 3 دعاة جدد لبرنامج الجاليات", time: "منذ 3 ساعات", type: "info" },
  { text: "طلب صيانة مسجد الفرقان — بانتظار الاعتماد", time: "منذ 5 ساعات", type: "warning" },
  { text: "تم تحصيل تبرعات رمضان بقيمة 680,000 ريال", time: "أمس", type: "success" },
  { text: "تحديث بيانات 12 موظف في الموارد البشرية", time: "أمس", type: "info" },
  { text: "اجتماع مجلس الإدارة رقم 24 — تم التوثيق", time: "منذ يومين", type: "success" },
];

const UPCOMING_EVENTS = [
  { title: "ملتقى الجاليات الثالث", day: "15", monthName: "رجب", location: "قاعة الملك فهد", time: "6:00م", type: "ملتقى", color: "#2563eb" },
  { title: "دورة فقه الصيام", day: "22", monthName: "شعبان", location: "مسجد التوحيد", time: "8:30م", type: "دورة", color: "#0891b2" },
  { title: "حفل تكريم المتطوعين", day: "5", monthName: "رمضان", location: "فندق موفنبيك", time: "7:00م", type: "حفل", color: "#059669" },
  { title: "اجتماع مجلس الإدارة 25", day: "10", monthName: "رمضان", location: "مقر الجمعية", time: "4:00م", type: "اجتماع", color: "#7c3aed" },
];

const DEPT_SUMMARY = [
  { name: "الجاليات", icon: Globe, stat: "5,200 مستفيد • 12 برنامج", trend: "+8%", color: "#2563eb" },
  { name: "المساجد", icon: Landmark, stat: "47 مسجد • 18 داعية", trend: "+2", color: "#0891b2" },
  { name: "النسائي", icon: Heart, stat: "1,800 مستفيدة • 8 برامج", trend: "+15%", color: "#db2777" },
  { name: "المالية", icon: Wallet, stat: "2.45M إيراد • 1.87M مصروف", trend: "+12%", color: "#16a34a" },
  { name: "الموارد البشرية", icon: Briefcase, stat: "64 موظف • 4 أقسام", trend: "0", color: "#0284c7" },
  { name: "التطوع", icon: HandHelping, stat: "186 متطوع • 22 فريق", trend: "+22", color: "#059669" },
];

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  /* Welcome */
  welcomeBanner: {
    background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    borderRadius: 16,
    padding: "28px 32px",
    marginBottom: 24,
    position: "relative",
    overflow: "hidden",
    color: C.white,
  },
  welcomeContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    zIndex: 1,
    flexWrap: "wrap",
    gap: 16,
  },
  welcomeTitle: {
    fontSize: 22,
    fontWeight: 700,
    margin: "0 0 4px",
  },
  welcomeSubtitle: {
    fontSize: 14,
    opacity: 0.8,
    margin: 0,
  },
  quoteCard: {
    background: "rgba(255,255,255,0.12)",
    borderRadius: 10,
    padding: "12px 20px",
    maxWidth: 400,
    borderRight: `3px solid ${C.accent}`,
  },
  quoteText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.8,
    opacity: 0.95,
  },
  welcomeDeco: {
    position: "absolute",
    top: -40,
    left: -40,
    width: 200,
    height: 200,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
  },

  /* KPI */
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 16,
    marginBottom: 24,
  },

  /* Charts */
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20,
    marginBottom: 20,
  },
  chartCard: {
    padding: 20,
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  chartTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: C.primary,
  },
  chartYear: {
    fontSize: 12,
    color: C.textLight,
    background: C.bg,
    padding: "4px 10px",
    borderRadius: 6,
  },

  /* Legend */
  legendGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "center",
    marginTop: 8,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  /* Quick Actions */
  quickGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 10,
  },
  quickBtn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    padding: 14,
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    cursor: "pointer",
    fontFamily: "'Readex Pro', sans-serif",
    transition: "all 0.2s",
  },
  quickIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  quickLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: C.primary,
    textAlign: "center",
  },

  /* Activity */
  activityList: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  activityItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 0",
    borderBottom: `1px solid ${C.bg}`,
  },
  activityDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginTop: 4,
    flexShrink: 0,
  },
  activityText: {
    margin: 0,
    fontSize: 13,
    color: C.primary,
    fontWeight: 500,
    lineHeight: 1.5,
  },
  activityTime: {
    fontSize: 11,
    color: C.textLight,
  },

  /* Events */
  eventsList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  eventItem: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    padding: 12,
    background: C.bg,
    borderRadius: 10,
  },
  eventDate: {
    width: 52,
    height: 52,
    borderRadius: 10,
    background: C.white,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid",
    flexShrink: 0,
  },
  eventDay: {
    fontSize: 18,
    fontWeight: 700,
    color: C.primary,
    lineHeight: 1,
  },
  eventMonth: {
    fontSize: 10,
    color: C.textLight,
    marginTop: 2,
  },
  eventTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: C.primary,
  },
  eventDetail: {
    margin: "4px 0 0",
    fontSize: 12,
    color: C.textLight,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  eventTypeBadge: {
    padding: "4px 10px",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  /* Department Summary */
  deptGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 20,
    marginTop: 12,
  },
  deptCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 14,
    background: C.bg,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
  },
  deptIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  deptName: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: C.primary,
  },
  deptStat: {
    margin: "2px 0 0",
    fontSize: 12,
    color: C.textLight,
  },
  deptTrend: {
    fontSize: 13,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 2,
    marginRight: "auto",
  },
};
